import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Tag, StatCard } from "@/components/Layout";
import { attendanceTone } from "@/lib/statusTone";
import { Button } from "@/components/ui/button";
import useBulkSelection from "@/hooks/useBulkSelection";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LogIn, LogOut, Users as UsersIcon, Pencil } from "lucide-react";
import BulkUploadDialog from "@/components/BulkUploadDialog";
import AttendanceInsights from "@/components/AttendanceInsights";
import AttendanceExportDialog from "@/components/AttendanceExportDialog";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { formatDate, formatTime } from "@/lib/dateFormat";
const fmtDate = (d) => formatDate(d, { fallback: "" });
const fmtTime = (t) => formatTime(t, { fallback: "—" });
// Records are stored as naive "YYYY-MM-DDTHH:MM:SS" IST wall-clock strings
// (see backend now_ist_naive_iso()) — slicing to 16 chars is exactly the
// <input type="datetime-local"> value format, no timezone math needed.
const toDatetimeLocal = (iso) => (iso ? iso.slice(0, 16) : "");
// Matches backend _ATTENDANCE_LATE_THRESHOLD — any check-in strictly after
// 10:30 AM IST is flagged late.
const isLateCheckin = (checkIn) => !!checkIn && checkIn.length >= 16 && checkIn.slice(11, 16) > "10:30";

/** Compute "HH:MM" worked-duration between two timestamps. */
function formatDuration(checkIn, checkOut) {
  if (!checkIn || !checkOut) return "—";
  const ci = new Date(checkIn);
  const co = new Date(checkOut);
  if (Number.isNaN(ci.getTime()) || Number.isNaN(co.getTime()) || co < ci) return "—";
  const minutes = Math.round((co - ci) / 60000);
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

function WeekCalendar() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/attendance/week").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const names = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const totalMissing = data.days.reduce((a, d) => a + (d.missing_minutes || 0), 0);
  return (
    <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60">
      <div className="flex items-center justify-between mb-3">
        <div className="label-overline">This week</div>
        <Tag tone={totalMissing > 0 ? "red" : "green"}>
          {totalMissing > 0 ? `${Math.round(totalMissing / 60 * 10) / 10}h potential salary deduction` : "Fully logged"}
        </Tag>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {data.days.map((d, i) => {
          const has = !!d.check_in;
          const missing = d.missing_minutes;
          let cls = "bg-slate-200";
          if (has && missing > 0) cls = "bg-orange-100 border border-orange-300";
          else if (has) cls = "bg-green-100 border border-green-300";
          return (
            <div key={d.date} className={`rounded-sm p-2 ${cls}`} title={d.date}>
              <div className="text-[10px] font-semibold text-slate-600">{names[i]}</div>
              <div className="text-[10px] text-slate-500">{d.date.slice(5)}</div>
              {has ? (
                <>
                  <div className="text-[10px] mt-1 font-mono">{Math.floor(d.minutes / 60)}h {d.minutes % 60}m</div>
                  {missing > 0 && <div className="text-[10px] text-red-700">−{Math.floor(missing / 60)}h {missing % 60}m</div>}
                </>
              ) : <div className="text-[10px] mt-1 text-slate-400">no check-in</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Attendance() {
  const { user } = useAuth();
  const can = usePermissions();
  const isAdmin = can("attendance_admin");
  const [today, setToday] = useState(null);
  const [rows, setRows] = useState([]);
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [overrideCheckIn, setOverrideCheckIn] = useState("");
  const [overrideCheckOut, setOverrideCheckOut] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overriding, setOverriding] = useState(false);
  const [overrideError, setOverrideError] = useState("");

  const load = async () => {
    const [t, l] = await Promise.all([
      api.get("/attendance/today"),
      api.get("/attendance"),
    ]);
    setToday(t.data && t.data.id ? t.data : null);
    setRows(l.data);
  };
  useEffect(() => { load(); }, []);

  const checkin = async () => {
    try { await api.post("/attendance/checkin"); toast.success("Checked in"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const checkout = async () => {
    try { await api.post("/attendance/checkout"); toast.success("Checked out"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  // Phase 84 — clear a mis-set check-out timestamp.
  const undoCheckout = async () => {
    if (!window.confirm("Clear today's check-out timestamp?")) return;
    try { await api.post("/attendance/checkout/undo"); toast.success("Check-out cleared"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openOverride = (r) => {
    setOverrideTarget(r);
    setOverrideCheckIn(toDatetimeLocal(r.check_in));
    setOverrideCheckOut(toDatetimeLocal(r.check_out));
    setOverrideReason("");
    setOverrideError("");
  };
  const submitOverride = async () => {
    if (!overrideReason.trim()) { setOverrideError("A reason is required for a manual attendance change"); return; }
    if (overrideCheckIn && overrideCheckOut && overrideCheckOut < overrideCheckIn) {
      setOverrideError("Check-out can't be before check-in");
      return;
    }
    setOverrideError("");
    setOverriding(true);
    try {
      const body = { reason: overrideReason.trim() };
      const origIn = toDatetimeLocal(overrideTarget.check_in);
      const origOut = toDatetimeLocal(overrideTarget.check_out);
      if (overrideCheckIn !== origIn) body.check_in = overrideCheckIn ? `${overrideCheckIn}:00` : "";
      if (overrideCheckOut !== origOut) body.check_out = overrideCheckOut ? `${overrideCheckOut}:00` : "";
      await api.patch(`/attendance/${overrideTarget.id}/override`, body);
      toast.success(`Updated ${overrideTarget.user_name || "attendance"} record for ${fmtDate(overrideTarget.date)}`);
      setOverrideTarget(null);
      load();
    } catch (e) { setOverrideError(formatApiError(e)); toast.error(formatApiError(e)); }
    finally { setOverriding(false); }
  };

  // For admin: split into "my" rows vs "team" rows.
  const myRows = useMemo(
    () => rows.filter((r) => r.user_id === user?.id),
    [rows, user?.id],
  );
  const teamRows = useMemo(
    () => (isAdmin ? rows.filter((r) => r.user_id !== user?.id) : []),
    [rows, isAdmin, user?.id],
  );
  const presentDays = myRows.filter((r) => r.check_in).length;

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto fade-in" data-testid="attendance-page">
      <PageHeader
        eyebrow="Daily"
        title="Attendance"
        subtitle="Tap in when you start, tap out when you wrap. We log timestamps with timezone."
        actions={(
          <div className="flex items-center gap-2">
            {isAdmin && <AttendanceExportDialog />}
            <BulkUploadDialog section="attendance" label="Attendance" onCommitted={load} />
          </div>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="card-flat p-6">
          <div className="label-overline">Today</div>
          <div className="mt-3 font-display text-2xl font-bold text-slate-900">{today?.check_in ? `In · ${fmtTime(today.check_in)}` : "Not checked in"}</div>
          <div className="text-xs text-slate-500 mt-1">{today?.check_out ? `Out · ${fmtTime(today.check_out)}` : "Still in studio"}</div>
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <Button data-testid="checkin-btn" onClick={checkin} disabled={!!today?.check_in} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"><LogIn size={14} className="mr-1.5" /> Check in</Button>
            {/* Phase 84 — allow re-check-out to override a mis-set timestamp. */}
            <Button data-testid="checkout-btn" onClick={checkout} disabled={!today?.check_in} variant="outline" className="rounded-sm">
              <LogOut size={14} className="mr-1.5" /> {today?.check_out ? "Update check-out" : "Check out"}
            </Button>
            {today?.check_out && (
              <button
                type="button"
                onClick={undoCheckout}
                className="text-[11px] text-red-600 hover:underline"
                data-testid="attendance-undo-checkout"
                title="Clear this check-out timestamp"
              >
                Undo
              </button>
            )}
          </div>
        </div>
        <StatCard label="Present (30d)" value={presentDays} />
        <StatCard label="Avg duration" value={"~8.2h"} hint="Rough estimate" />
      </div>

      {isAdmin && (
        <div className="mb-8">
          <AttendanceInsights rows={rows} onModify={openOverride} />
        </div>
      )}

      <div className="card-flat">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display font-bold text-slate-900">Recent days</div>
          <Tag tone="slate">Last 30</Tag>
        </div>
        <WeekCalendar />
        <div className="divide-row">
          {myRows.map((r) => (
            <div key={r.id} className="px-5 py-3 grid grid-cols-4 gap-4 text-sm">
              <div className="text-slate-700">{fmtDate(r.date)}</div>
              <div>{fmtTime(r.check_in)}</div>
              <div>{fmtTime(r.check_out)}</div>
              <div className="text-right flex items-center justify-end gap-2">
                {(() => {
                  const tone = attendanceTone(r);
                  if (r.check_out) return <Tag tone={tone}>Complete</Tag>;
                  if (r.check_in) return <Tag tone={tone}>Open</Tag>;
                  return <Tag tone={tone}>—</Tag>;
                })()}
                {isLateCheckin(r.check_in) && <Tag tone="orange">Late</Tag>}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => openOverride(r)}
                    className="text-slate-400 hover:text-slate-700"
                    title="Modify this attendance record"
                    data-testid={`attendance-modify-btn-${r.id}`}
                  >
                    <Pencil size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {myRows.length === 0 && <div className="p-5 text-sm text-slate-500">No attendance records yet.</div>}
        </div>
      </div>

      {isAdmin && <TeamAttendanceTable rows={teamRows} onModify={openOverride} />}

      <Dialog open={!!overrideTarget} onOpenChange={(o) => !o && setOverrideTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="attendance-override-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Modify attendance record</DialogTitle>
          </DialogHeader>
          {overrideTarget && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">
                {overrideTarget.user_name || "You"} · {fmtDate(overrideTarget.date)} — founder/admin-only correction, logged to the audit log.
              </div>
              <div>
                <Label className="label-overline">Check-in time</Label>
                <Input
                  type="datetime-local"
                  className="rounded-sm mt-1"
                  value={overrideCheckIn}
                  onChange={(e) => setOverrideCheckIn(e.target.value)}
                  data-testid="attendance-override-checkin"
                />
              </div>
              <div>
                <Label className="label-overline">Check-out time</Label>
                <Input
                  type="datetime-local"
                  className="rounded-sm mt-1"
                  value={overrideCheckOut}
                  onChange={(e) => setOverrideCheckOut(e.target.value)}
                  data-testid="attendance-override-checkout"
                />
              </div>
              <div>
                <Label className="label-overline">Reason (required)</Label>
                <Textarea
                  rows={2}
                  className="rounded-sm mt-1"
                  placeholder="e.g. forgot to check out, corrected from swipe-card log"
                  value={overrideReason}
                  onChange={(e) => { setOverrideReason(e.target.value); setOverrideError(""); }}
                  data-testid="attendance-override-reason"
                />
              </div>
              {overrideError && (
                <div className="text-xs text-red-600" data-testid="attendance-override-error">{overrideError}</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={submitOverride}
              disabled={overriding}
              className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="attendance-override-submit"
            >
              {overriding ? "Saving…" : "Save change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Admin-only: every team member's attendance in a flat table.
 *  Columns: EMPLOYEE · DATE · TIME IN · TIME OUT · TOTAL · STATUS.
 *  Feb 2026 · Paginated (50 rows/page) so a workspace with 660+ attendance
 *  rows doesn't render the whole list up front. Filter re-applies before
 *  pagination so search results always start on page 1. */
function TeamAttendanceTable({ rows, onModify }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return rows;
    return rows.filter((r) =>
      (r.user_name || "").toLowerCase().includes(ql) ||
      (r.date || "").includes(ql),
    );
  }, [rows, q]);
  // Reset to page 1 whenever the filter changes so results are always visible.
  useEffect(() => { setPage(1); }, [q]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);
  const startIdx = (clampedPage - 1) * PAGE_SIZE;
  const paged = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  const bulk = useBulkSelection({
    collection: "attendance",
    ids: paged.map((r) => r.id),
    onDone: () => window.location.reload(),
  });

  return (
    <div className="card-flat mt-8" data-testid="team-attendance">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <UsersIcon size={14} className="text-slate-500" />
          <div className="font-display font-bold text-slate-900">Team attendance</div>
          <Tag tone="slate">{filtered.length} records</Tag>
          {filtered.length > PAGE_SIZE && (
            <span className="text-[11px] text-slate-500" data-testid="team-attendance-page-label">
              Showing {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, filtered.length)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by name or date…"
            className="rounded-sm h-8 text-xs w-56"
            data-testid="team-attendance-search"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {bulk.canBulk && (<th className="w-8 px-2 py-2"><bulk.HeaderCheckbox /></th>)}
              {["EMPLOYEE", "DATE", "TIME IN", "TIME OUT", "TOTAL", "STATUS", ""].map((h) => (
                <th key={h} className="label-overline px-4 py-2 text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paged.map((r) => {
              const tone = attendanceTone(r);
              let label = "—";
              if (r.check_out) label = "Complete";
              else if (r.check_in) label = "Open";
              return (
                <tr key={r.id} className={`hover:bg-slate-50 ${bulk.isSelected(r.id) ? "bg-orange-50/40" : ""}`} data-testid={`team-att-${r.id}`}>
                  {bulk.canBulk && (<td className="w-8 px-2 py-2"><bulk.RowCheckbox id={r.id} /></td>)}
                  <td className="px-4 py-2 text-slate-800 whitespace-nowrap font-medium">{r.user_name || "—"}</td>
                  <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-4 py-2 font-mono text-slate-700 whitespace-nowrap">{fmtTime(r.check_in)}</td>
                  <td className="px-4 py-2 font-mono text-slate-700 whitespace-nowrap">{fmtTime(r.check_out)}</td>
                  <td className="px-4 py-2 font-mono text-slate-700 whitespace-nowrap">{formatDuration(r.check_in, r.check_out)}</td>
                  <td className="px-4 py-2"><Tag tone={tone}>{label}</Tag> {isLateCheckin(r.check_in) && <Tag tone="orange">Late</Tag>}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => onModify(r)}
                      className="text-slate-400 hover:text-slate-700"
                      title="Modify this attendance record"
                      data-testid={`attendance-modify-btn-${r.id}`}
                    >
                      <Pencil size={13} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={bulk.canBulk ? 8 : 7} className="px-4 py-6 text-center text-xs text-slate-400">No team attendance records match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600" data-testid="team-attendance-pager">
          <div>Page {clampedPage} of {totalPages}</div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline" size="sm"
              className="h-7 px-2 text-xs rounded-sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={clampedPage === 1}
              data-testid="team-attendance-prev"
            >
              Prev
            </Button>
            <Button
              variant="outline" size="sm"
              className="h-7 px-2 text-xs rounded-sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={clampedPage === totalPages}
              data-testid="team-attendance-next"
            >
              Next
            </Button>
          </div>
        </div>
      )}
      <bulk.ActionBar />
      <bulk.ConfirmDialog />
    </div>
  );
}
